import { Stack, useMediaQuery, useTheme } from "@mui/material";

import Datetime from "@/common/components/Datetime/Datetime";
import InlineUser from "@/common/components/Entity/User/InlineUser";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import FieldValue from "@/common/components/Form/Display/FieldValue";
import { TypeHelper } from "@/common/helpers/type";
import { ApiEnumName } from "@/common/services/enum";
import { DamageCorrectionRole, VehicleDamageCorrectionDto } from "@/core/api/generated/v1";

import VehicleDamageLink from "./VehicleDamageLink";

interface Props {
  correction: Nil<VehicleDamageCorrectionDto>;
}

export default function VehicleDamageCorrectionDisplay({ correction }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isSourceRole = correction?.role === DamageCorrectionRole.Source;
  const isTargetRole = correction?.role === DamageCorrectionRole.Target;
  const sourceOrTargetDamageIds = isSourceRole
    ? correction?.targetDamageIds
    : isTargetRole
      ? correction?.sourceDamageIds
      : null;

  return (
    <Stack gap={1}>
      <FieldValue label='Type' isEmpty={!correction?.type}>
        <InlineApiEnumValue
          type={ApiEnumName.DamageCorrectionType}
          value={correction?.type}
          withHelperTooltip
        />
      </FieldValue>

      <FieldValue label='Role' isEmpty={!correction?.role}>
        <InlineApiEnumValue
          type={ApiEnumName.DamageCorrectionRole}
          value={correction?.role}
          withHelperTooltip
        />
      </FieldValue>

      <FieldValue
        label={isSourceRole ? "Target damage(s)" : isTargetRole ? "Source damage(s)" : "-"}
        isEmpty={TypeHelper.isEmptyArray(sourceOrTargetDamageIds)}
      >
        <Stack gap={0}>
          {sourceOrTargetDamageIds?.map((vehicleDamageId) => (
            <VehicleDamageLink
              key={vehicleDamageId}
              vehicleId={undefined}
              entityId={vehicleDamageId}
              entity={undefined}
            />
          ))}
        </Stack>
      </FieldValue>

      <FieldValue
        label='Created at'
        direction={isMobile ? "column" : "row"}
        isEmpty={!correction?.createdAt}
      >
        <Datetime datetime={correction?.createdAt} withDurationFromNow={false} />
      </FieldValue>

      <FieldValue label='Created by' isEmpty={!correction?.createdBy}>
        <InlineUser userId={correction?.createdBy} withAvatar withLink />
      </FieldValue>
    </Stack>
  );
}
